import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Handshake, MessagesSquare, ChevronRight, Check, Lock, Eye, EyeOff, Users2,
  Sparkles, Flag, Ban, ArrowLeft,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { FullSpinner, Empty } from "@/components/States";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const CMP = {
  aligned: { label: "Aligned", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  partial: { label: "Partially aligned", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  different: { label: "Different", cls: "bg-rose-50 text-rose-600 border-rose-200" },
  discuss: { label: "Discuss together", cls: "bg-[#FAF3E0] text-[#9E7B1B] border-[#EBD9A6]" },
  awaiting: { label: "Awaiting their answer", cls: "bg-slate-50 text-slate-500 border-slate-200" },
  not_answered: { label: "Not answered", cls: "bg-slate-50 text-slate-400 border-slate-200" },
};
const VIS = [
  { v: "private", label: "Keep private", hint: "Only you can see this", icon: EyeOff },
  { v: "shared", label: "Share now", hint: "They can see it right away", icon: Eye },
  { v: "mutual", label: "Reveal when both answer", hint: "Fair mutual exchange", icon: Users2 },
];

function fmtVal(q, v, preferNot) {
  if (preferNot) return "Prefer not to answer";
  if (v == null || v === "") return "—";
  if (Array.isArray(v)) return v.join(", ");
  if (q.type === "scale") return `${v} / ${q.scale?.max ?? 5}`;
  return String(v);
}

export default function Understanding() {
  const { target } = useParams();
  if (!target) return <ConnectionPicker />;
  return <Session target={target} />;
}

/* -------------------------------------------------- picker */
function ConnectionPicker() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/jodi/understanding/connections").then(({ data }) => setData(data.connections)).catch(() => setData([])); }, []);
  return (
    <div className="space-y-6" data-testid="understanding-picker">
      <header>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#58111A]">Jodi Understanding Hub</h1>
        <p className="text-sm text-[#605255] mt-1 max-w-xl">Understand each other before making important decisions. Share what you value, expect, and hope for — you both stay in control of what's revealed.</p>
      </header>
      {!data ? <FullSpinner /> : data.length === 0 ? (
        <Empty icon={Handshake} title="No connections yet" message="Once you connect with someone (interest accepted), you can start understanding each other here." />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {data.map((p) => (
            <button key={p.user_id} onClick={() => nav(`/app/jodi/understanding/${p.user_id}`)} data-testid={`uh-conn-${p.user_id}`}
              className="mj-card p-4 flex items-center gap-3 text-left hover:border-[#C59B27] transition">
              <Avatar photos={p.photos} name={p.full_name} size={56} className="rounded-xl w-14 h-14" />
              <div className="flex-1 min-w-0">
                <div className="font-serif font-semibold text-[#58111A]">{p.full_name}{p.age ? `, ${p.age}` : ""}</div>
                <div className="text-xs text-[#605255]">{p.understanding?.my_answered || 0}/{p.understanding?.total || 0} topics answered</div>
              </div>
              <ChevronRight className="w-4 h-4 text-[#C59B27]" />
            </button>
          ))}
        </div>
      )}
      <p className="text-xs text-[#9E7B1B] text-center pt-4">This is not a match score. It's a space to understand each other — the decision is always yours.</p>
    </div>
  );
}

/* -------------------------------------------------- session */
function Session({ target }) {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [cat, setCat] = useState(null);
  const [reportQ, setReportQ] = useState(null);
  const load = () => api.get(`/jodi/understanding/session/${target}`).then(({ data }) => {
    setD(data); setCat((c) => c || (data.categories[0]?.key));
  }).catch((e) => { toast.error(apiError(e)); nav("/app/jodi/understanding"); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [target]);

  if (!d) return <FullSpinner label="Opening session…" />;
  const stopped = d.status === "stopped";
  const activeCat = d.categories.find((c) => c.key === cat) || d.categories[0];
  const toggleStop = async () => {
    try { await api.post(`/jodi/understanding/${stopped ? "resume" : "stop"}/${target}`); toast.success(stopped ? "Session resumed" : "Session stopped"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="understanding-session">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <button onClick={() => nav("/app/jodi/understanding")} className="p-2 rounded-lg hover:bg-[#FAF3E0]" data-testid="uh-back"><ArrowLeft className="w-4 h-4 text-[#58111A]" /></button>
          <Avatar photos={d.target_profile?.photos} name={d.target_profile?.full_name} size={48} className="rounded-full w-12 h-12" />
          <div>
            <h1 className="font-serif text-2xl font-bold text-[#58111A]">Understanding {d.target_profile?.full_name?.split(" ")[0]}</h1>
            <p className="text-xs text-[#605255]">{d.progress.answered}/{d.progress.total} topics answered · {d.progress.pct}% complete</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {d.connected && <button onClick={() => nav(`/app/messages/${target}`)} data-testid="uh-discuss-chat" className="text-sm px-3 py-2 rounded-xl border border-[#E8DFD1] text-[#781826] flex items-center gap-1.5"><MessagesSquare className="w-4 h-4" /> Discuss in Chat</button>}
          <button onClick={toggleStop} data-testid="uh-stop" className="text-sm px-3 py-2 rounded-xl border border-[#E8DFD1] text-[#605255] flex items-center gap-1.5"><Ban className="w-4 h-4" /> {stopped ? "Resume" : "Stop"}</button>
        </div>
      </div>

      {stopped && <div className="mj-card p-3 text-sm text-[#BE123C] bg-[#FBEBE1]">This session is stopped. Resume it to continue answering.</div>}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* main */}
        <div className="lg:col-span-2 space-y-4">
          {/* category tabs */}
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid="uh-cat-tabs">
            {d.categories.map((c) => {
              const pc = d.progress.by_category[c.key];
              return (
                <button key={c.key} onClick={() => setCat(c.key)} data-testid={`uh-cat-${c.key}`}
                  className={`px-3 py-2 rounded-xl text-xs font-medium whitespace-nowrap border ${cat === c.key ? "bg-[#781826] text-white border-[#781826]" : "border-[#E8DFD1] text-[#605255]"}`}>
                  {c.label} {pc ? <span className="opacity-70">{pc.pct}%</span> : null}
                </button>
              );
            })}
          </div>

          {activeCat?.questions.map((q) => (
            <QuestionCard key={q.id} q={q} target={target} disabled={stopped} onSaved={load} onReport={() => setReportQ(q)} />
          ))}
        </div>

        {/* sidebar: progress + summary */}
        <div className="space-y-4">
          <div className="mj-card p-4" data-testid="uh-progress">
            <h3 className="font-serif font-semibold text-[#58111A] mb-3">Understanding progress</h3>
            {d.categories.map((c) => {
              const pc = d.progress.by_category[c.key] || { pct: 0 };
              return (
                <div key={c.key} className="mb-2">
                  <div className="flex justify-between text-[11px] text-[#605255]"><span>{c.label}</span><span>{pc.pct}%</span></div>
                  <div className="h-1.5 rounded-full bg-[#F1E9DA] mt-0.5"><div className="h-1.5 rounded-full bg-[#C59B27]" style={{ width: `${pc.pct}%` }} /></div>
                </div>
              );
            })}
          </div>

          <SummaryBlock title="Areas that align" items={d.summary.aligned} tone="emerald" empty="No aligned topics revealed yet." testid="uh-aligned" />
          <SummaryBlock title="Areas that differ" items={d.summary.different} tone="rose" empty="No differences revealed yet." testid="uh-different" />
          <div className="mj-card p-4" data-testid="uh-discuss">
            <h3 className="font-serif font-semibold text-[#58111A] mb-2 flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-[#C59B27]" /> {d.has_meeting ? "Topics to discuss before your meeting" : "Things you may want to discuss"}</h3>
            {d.summary.discuss.length === 0 ? <p className="text-xs text-[#605255]">Answer a few topics to see suggestions.</p> : (
              <ul className="space-y-1.5">
                {d.summary.discuss.map((t, i) => <li key={i} className="text-sm text-[#261A1C] flex items-start gap-1.5"><ChevronRight className="w-3.5 h-3.5 text-[#C59B27] mt-0.5 shrink-0" /> {t}</li>)}
              </ul>
            )}
            {d.connected && d.summary.discuss.length > 0 && (
              <button onClick={() => nav(`/app/messages/${target}`)} className="mt-3 btn-primary text-xs w-full"><MessagesSquare className="w-3.5 h-3.5" /> Discuss in Chat</button>
            )}
          </div>
        </div>
      </div>

      <p className="text-xs text-[#9E7B1B] text-center pt-2">ManaJodi presents facts you both chose to share. There's no winner or score — the decision is yours.</p>
      {reportQ && <ReportDialog q={reportQ} onClose={() => setReportQ(null)} />}
    </div>
  );
}

function SummaryBlock({ title, items, empty, testid }) {
  return (
    <div className="mj-card p-4" data-testid={testid}>
      <h3 className="font-serif font-semibold text-[#58111A] mb-2">{title}</h3>
      {items.length === 0 ? <p className="text-xs text-[#605255]">{empty}</p> : (
        <ul className="space-y-1.5">{items.map((t, i) => <li key={i} className="text-sm text-[#261A1C] flex items-start gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" /> {t}</li>)}</ul>
      )}
    </div>
  );
}

/* -------------------------------------------------- question card */
function QuestionCard({ q, target, disabled, onSaved, onReport }) {
  const mine = q.my_answer;
  const [value, setValue] = useState(mine?.value ?? (q.type === "multi" ? [] : ""));
  const [visibility, setVisibility] = useState(mine?.visibility || q.visibility_default || "mutual");
  const [preferNot, setPreferNot] = useState(!!mine?.prefer_not);
  const [saving, setSaving] = useState(false);
  const cmp = CMP[q.comparison] || CMP.not_answered;

  const toggleMulti = (opt) => setValue((v) => (Array.isArray(v) && v.includes(opt)) ? v.filter((x) => x !== opt) : [...(v || []), opt]);
  const canSave = preferNot || (q.type === "multi" ? (value && value.length) : (value !== "" && value != null));

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/jodi/understanding/answer", { target, question_id: q.id, value: preferNot ? null : value, visibility, prefer_not: preferNot });
      toast.success("Answer saved"); onSaved && onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="mj-card p-4 space-y-3" data-testid={`uh-q-${q.id}`}>
      <div className="flex items-start justify-between gap-2">
        <h4 className="font-medium text-[#261A1C] text-sm">{q.text}</h4>
        <button onClick={onReport} title="Report question" className="text-slate-300 hover:text-[#BE123C] shrink-0"><Flag className="w-3.5 h-3.5" /></button>
      </div>

      {q.profile_value && !mine && (
        <button onClick={() => { setValue(q.type === "multi" ? [String(q.profile_value)] : String(q.profile_value)); setPreferNot(false); }}
          className="text-[11px] px-2 py-1 rounded-full bg-[#FAF3E0] text-[#9E7B1B]">Already in your profile: <b>{String(q.profile_value)}</b> · use this</button>
      )}

      {!disabled && !preferNot && (
        <div>
          {q.type === "single" && (
            <div className="flex flex-wrap gap-1.5">
              {q.options.map((o) => (
                <button key={o} onClick={() => setValue(o)} data-testid={`uh-opt-${q.id}-${o.replace(/\W+/g, "")}`}
                  className={`text-xs px-3 py-1.5 rounded-full border ${value === o ? "bg-[#781826] text-white border-[#781826]" : "border-[#E8DFD1] text-[#605255]"}`}>{o}</button>
              ))}
            </div>
          )}
          {q.type === "multi" && (
            <div className="flex flex-wrap gap-1.5">
              {q.options.map((o) => (
                <button key={o} onClick={() => toggleMulti(o)}
                  className={`text-xs px-3 py-1.5 rounded-full border ${Array.isArray(value) && value.includes(o) ? "bg-[#781826] text-white border-[#781826]" : "border-[#E8DFD1] text-[#605255]"}`}>{o}</button>
              ))}
            </div>
          )}
          {q.type === "scale" && (
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-[#9E7B1B]">{q.scale?.min_label}</span>
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => setValue(n)}
                  className={`w-8 h-8 rounded-full border text-xs font-semibold ${Number(value) === n ? "bg-[#781826] text-white border-[#781826]" : "border-[#E8DFD1] text-[#605255]"}`}>{n}</button>
              ))}
              <span className="text-[10px] text-[#9E7B1B]">{q.scale?.max_label}</span>
            </div>
          )}
          {q.type === "text" && (
            <textarea value={value || ""} onChange={(e) => setValue(e.target.value)} rows={2} placeholder="Share in your own words…" className="w-full border border-[#E8DFD1] rounded-xl p-2.5 text-sm" />
          )}
        </div>
      )}

      {!disabled && (
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1.5 text-[11px] text-[#605255] cursor-pointer">
            <input type="checkbox" checked={preferNot} onChange={(e) => setPreferNot(e.target.checked)} className="w-3.5 h-3.5 accent-[#781826]" /> Prefer not to answer
          </label>
          <select value={visibility} onChange={(e) => setVisibility(e.target.value)} data-testid={`uh-vis-${q.id}`} className="text-[11px] border border-[#E8DFD1] rounded-lg px-2 py-1.5 ml-auto">
            {VIS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
          <button disabled={!canSave || saving} onClick={save} data-testid={`uh-save-${q.id}`} className="btn-primary text-xs px-3 py-1.5 disabled:opacity-50">{mine ? "Update" : "Save"}</button>
        </div>
      )}

      {/* answers + comparison */}
      {(mine || q.their_answered) && (
        <div className="pt-2 border-t border-[#F1E9DA] grid grid-cols-2 gap-2 text-xs">
          <div>
            <div className="text-[10px] text-[#9E7B1B] uppercase">You</div>
            <div className="text-[#261A1C]">{mine ? fmtVal(q, mine.value, mine.prefer_not) : "—"}</div>
          </div>
          <div>
            <div className="text-[10px] text-[#9E7B1B] uppercase">Them</div>
            {q.their_answer ? <div className="text-[#261A1C]">{fmtVal(q, q.their_answer.value, q.their_answer.prefer_not)}</div>
              : q.their_locked ? <div className="text-slate-400 flex items-center gap-1"><Lock className="w-3 h-3" /> Reveals when you both answer</div>
              : <div className="text-slate-400">Not answered yet</div>}
          </div>
          {mine && q.their_answer && (
            <div className="col-span-2"><span className={`inline-block text-[10px] px-2 py-0.5 rounded-full border ${cmp.cls}`}>{cmp.label}</span></div>
          )}
        </div>
      )}
    </div>
  );
}

function ReportDialog({ q, onClose }) {
  const [reason, setReason] = useState("");
  const submit = async () => { try { await api.post("/jodi/understanding/report", { question_id: q.id, reason }); toast.success("Reported. Thank you."); onClose(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="uh-report-dialog">
        <DialogHeader><DialogTitle className="font-serif text-[#58111A] flex items-center gap-2"><Flag className="w-4 h-4 text-[#BE123C]" /> Report question</DialogTitle></DialogHeader>
        <p className="text-xs text-[#605255]">"{q.text}"</p>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="What's inappropriate about this question?" className="w-full border border-[#E8DFD1] rounded-xl p-2.5 text-sm" />
        <DialogFooter><button onClick={submit} data-testid="uh-report-submit" className="btn-primary text-sm">Submit report</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
